import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Crown, CreditCard } from 'lucide-react'
import { api } from '@/lib/api'
import { useAuth } from '@/context/AuthContext'
import { formatDate, formatPrice } from '@/lib/helpers'

export default function SettingsPremium() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [premiumStatus, setPremiumStatus] = useState<any>(null)
  const [payments, setPayments] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [status, history] = await Promise.all([
          api.premium.getStatus().catch(() => null),
          api.payments.history().catch(() => []),
        ])
        setPremiumStatus(status)
        setPayments(Array.isArray(history) ? history : [])
      } catch {
        // ignore
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [])

  const isPremium = user?.isPremium || premiumStatus?.isPremium
  const isTeacher = user?.role === 'teacher'

  if (isTeacher) {
    return (
      <div className="bg-white rounded-2xl md:rounded-3xl border border-gray-200 p-5 sm:p-7 md:p-9">
        <div className="flex flex-col items-center text-center gap-4 py-8">
          <div className="bg-primary-100 rounded-full p-4">
            <Crown className="size-8 text-primary-500" />
          </div>
          <h2 className="text-lg font-bold text-gray-800">Багш хэрэглэгч</h2>
          <p className="text-sm text-gray-500 max-w-sm">
            Та багш хэрэглэгч тул бүх контентэд бүрэн хандах эрхтэй. Premium эрх авах шаардлагагүй.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-2xl md:rounded-3xl border border-gray-200 p-5 sm:p-7 md:p-9">
      {/* Premium Status Card */}
      <div className="bg-white border border-gray-200 rounded-2xl md:rounded-[32px] p-4 sm:p-5 md:p-6 relative overflow-hidden mb-6 md:mb-9">
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#004da6] to-[#ff4d00] opacity-5" />

        <div className="relative flex flex-col gap-3 md:gap-4">
          <div className="flex items-center gap-3 md:gap-4">
            <div className="bg-primary-500 rounded-full p-2.5 md:p-3 shadow-sm">
              <Crown className="size-5 md:size-[26px] text-white" />
            </div>
            <h3 className="text-xl md:text-[30px] font-bold text-gray-800">Premium эрх</h3>
          </div>

          <p className="text-sm md:text-lg text-gray-600 max-w-[476px]">
            Та ARYA платформын бүх давуу талуудыг ашиглаж, хязгааргүй суралцах боломжтой.
          </p>

          <div className="flex flex-col sm:flex-row sm:items-center gap-3 md:gap-4">
            <button
              onClick={() => navigate('/premium')}
              className="bg-primary-500 text-white rounded-full px-5 md:px-6 py-2.5 md:py-3 text-sm font-semibold shadow-xs hover:bg-primary-600 transition cursor-pointer w-fit"
            >
              Эрх сунгах
            </button>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-gray-600">Одоогийн төлөв:</span>
              <span className={`font-semibold uppercase ${isPremium ? 'text-primary-500' : 'text-gray-400'}`}>
                {isPremium ? 'Идэвхтэй' : 'Идэвхгүй'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Divider */}
      <hr className="border-gray-200 mb-6 md:mb-9" />

      {/* Payment History */}
      <div className="flex flex-col gap-4 md:gap-6">
        <h3 className="text-base md:text-lg font-bold text-gray-800">Төлбөрийн түүх</h3>

        {loading ? (
          <div className="text-center py-6 md:py-8 text-gray-400 text-sm">Ачаалж байна...</div>
        ) : payments.length === 0 ? (
          <div className="text-center py-6 md:py-8 text-gray-400 text-sm">Төлбөрийн түүх байхгүй</div>
        ) : (
          <div className="flex flex-col gap-3 md:gap-4">
            {payments.map((payment: any, idx: number) => (
              <div key={payment._id || idx} className="flex items-center gap-3 md:gap-4">
                <div className="border border-gray-300 rounded-full p-2.5 md:p-3 shadow-xs shrink-0">
                  <CreditCard className="size-4 md:size-5 text-gray-600" />
                </div>
                <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                  <p className="text-sm md:text-base font-semibold text-gray-700 truncate">
                    {payment.courseId?.title || payment.type === 'subscription' ? 'Premium Багц' : 'Сургалт'}
                    {payment.type === 'subscription' && ' (1 сар)'}
                  </p>
                  <p className="text-[10px] md:text-xs text-gray-400 uppercase">
                    {payment.createdAt ? formatDate(payment.createdAt) : ''}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <p className="text-sm md:text-base font-semibold text-gray-700">
                    {formatPrice(payment.amount || 0)}
                  </p>
                  <div className={`px-1.5 py-0.5 md:py-1 rounded text-[10px] font-semibold uppercase ${
                    payment.status === 'completed'
                      ? 'bg-green-50 text-green-600'
                      : payment.status === 'pending'
                      ? 'bg-yellow-50 text-yellow-600'
                      : 'bg-red-50 text-red-600'
                  }`}>
                    {payment.status === 'completed' ? 'Амжилттай' : payment.status === 'pending' ? 'Хүлээгдэж буй' : 'Амжилтгүй'}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
